import { styled } from '@porsche-code/kado-styled-system/jsx';
import { ActionGroup, RichText, Spacer, Spinner } from '@porsche-code/kado-ui';
import { PButton, PHeading } from '@porsche-design-system/components-react';
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplicationTags, DateOutput } from '@/components';
import { useFeatureArticleQuery } from '@/graphql';

export const Route = createFileRoute('/news/$featureArticleId')({
  component: FeatureArticle,
  beforeLoad: () => {
    return {
      breadcrumb: () => <Breadcrumb />,
    };
  },
  validateSearch: (input) => {
    const preview = input.preview ? { preview: Number(input.preview) } : {};
    return { ...preview };
  },
});

const Breadcrumb = () => {
  const { featureArticleId: id } = useParams({
    from: '/news/$featureArticleId',
  });
  const { data } = useFeatureArticleQuery({ id });

  return <>{data?.featureArticle?.title}</>;
};

function FeatureArticle() {
  const { t } = useTranslation();

  const { featureArticleId } = useParams({ from: '/news/$featureArticleId' });
  const search: { preview?: boolean } = useSearch({
    from: '/news/$featureArticleId',
  });

  const { data: featureArticle, isPending } = useFeatureArticleQuery(
    {
      id: featureArticleId,
    },
    {
      select: (data) => data.featureArticle,
    },
  );

  useEffect(() => {
    if (featureArticle?.id && !search.preview) {
      // assume it is read if loaded
      localStorage.setItem(
        `kado.readFeatureArticle.${featureArticle.id}`,
        'true',
      );
    }
  }, [featureArticle?.id, search.preview]);

  if (isPending) {
    return <Spinner size="xl" />;
  }

  if (!featureArticle) {
    return null;
  }

  return (
    <Content>
      <PHeading size="lg" aria-level={1} tag="h1">
        {featureArticle.title}
      </PHeading>
      <Subtitle>
        <span>
          {featureArticle.publishedAt ? (
            <DateOutput date={featureArticle.publishedAt} />
          ) : (
            'n/a'
          )}
        </span>
        <span>{featureArticle.author.name}</span>
        {featureArticle.apps.length === 0 ? null : (
          <ApplicationTags apps={featureArticle.apps} />
        )}
      </Subtitle>
      <Spacer mb="sm" />
      <div>
        <RichText value={JSON.parse(featureArticle.article)} includeImages />
      </div>
      <ActionGroup>
        <PButton type="button" onClick={() => history.back()}>
          {t('back')}
        </PButton>
      </ActionGroup>
    </Content>
  );
}

const Content = styled('div', {
  base: {
    maxWidth: 'content',
  },
});

const Subtitle = styled('div', {
  base: {
    color: 'text.contrastMedium',
    display: 'grid',
    gridAutoColumns: 'max-content',
    gap: 'sm',
    gridAutoFlow: 'column',
    marginBottom: 'md',
  },
});
